/**
 * The console's base path — which is now the ORIGIN ROOT.
 *
 * ⛔ Three things must agree on this and nothing in the toolchain links them:
 * `vite.config.ts`'s `base`, `<BrowserRouter basename>`, and the `location` blocks in
 * `nginx.conf.template`. (The HTTPRoute `PathPrefix` used to be a fourth; it is `/` now and no
 * longer carries a prefix to agree about.) `src/__tests__/base-path.build.test.ts` is the
 * mechanical link, and it reads the BUILT `dist/index.html` rather than this constant —
 * asserting a constant against itself proves the constant, not that Vite applied it.
 *
 * It lives in `src/platform/` rather than in `src/shell/` because the http client needs it
 * (a session that cannot be refreshed navigates to the login page) and `src/platform/`
 * must not import from `src/shell/` — that is the wrong direction across the boundary.
 * `src/shell/CpAdminApp.tsx` re-exports `BASENAME` so the build test's import path matches
 * WS-A §6, while there is still exactly one definition.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * ## ⛔ WHY `BASENAME` IS RETAINED AS AN EMPTY STRING RATHER THAN DELETED
 *
 * It still has one consumer that needs a value rather than nothing: `main.tsx` and
 * `branding/BrandFollowsPolarity.tsx` pass it as `cpApplyBranding`'s `basePrefix`, whose
 * `setFavicon` is `href.startsWith('/') ? `${basePrefix}${href}` : href`. **Both of those
 * branches stay live at `basePrefix: ''`** — a root-relative `/a.png` and an absolute
 * `https://cdn…/a.png` are still different cases — so the parameter is not dead and neither is
 * this constant. `''` is also React Router's no-basename value, so `<BrowserRouter
 * basename={BASENAME}>` needs no change.
 *
 * ## ⛔ AND WHY THE TWO CONVERSION FUNCTIONS ARE GONE
 *
 * There used to be `toBrowserPath` and `toRouterPath`, because the app spoke two coordinate
 * systems: browser-absolute (`/cp-admin/apps/X`, what `window.location` reports and consumes)
 * and router-relative (`/apps/X`, what `<BrowserRouter basename>` matches). **At the origin
 * root there is exactly one coordinate system**, so both functions became the identity with
 * permanently-taken branches — un-coverable in a package held at 100% branches, and misleading
 * to anyone who read them as evidence that a prefix still existed. Deleted rather than
 * neutered.
 *
 * ⚠️ The hazard they existed to prevent is real and is now prevented by construction, not by a
 * function: a stashed browser path fed to `navigate()` used to land on `/cp-admin/cp-admin/…`.
 * The one surviving trace is the transitional legacy-prefix strip in `auth/return-to.ts`, which
 * exists for values written by a PREVIOUS deploy and is marked for removal next release.
 */

/**
 * ⛔ The EMPTY STRING, not `'/'`. `<BrowserRouter basename>` treats `''` as "no basename";
 * `'/'` would work by accident today but re-introduces the trailing-slash ambiguity this
 * constant exists to settle, and `${BASENAME}/login` would then be `//login` — a
 * protocol-relative URL, which is the exact shape `auth/return-to.ts` rejects as off-origin.
 */
export const BASENAME = '';

/**
 * Where an unauthenticated or expired session goes.
 *
 * ⛔ A BROWSER path handed to `window.location`, which knows nothing about the router. Written
 * as the LITERAL `'/login'` rather than `` `${BASENAME}/login` `` — interpolating an empty
 * string to produce a constant is indirection that reads as though a prefix might appear, and
 * this value is exactly what it says.
 */
export const LOGIN_URL = '/login';
